import { arktypeResolver } from "@hookform/resolvers/arktype";
import { ORPCError } from "@orpc/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { AgendaEvent } from "@/api/db/agenda";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";
import { Select } from "@/components/ui/select";
import { MAX_CARD_INSTALLMENTS } from "@/lib/card-installments";
import { djs } from "@/lib/djs";
import { AgendaLabels } from "@/lib/labels/agenda";

const PAYMENT_METHODS = {
	pix: "PIX",
	transfer: "Transferência",
	cash: "Espécie",
	debit_card: "Débito",
	credit_card: "Crédito",
} as const;
type LifecycleStatus = "confirmado" | "em_espera" | "em_atendimento" | "faltou";

const rescheduleForm = type({
	date: "string",
	start_time: "string",
	duration_minutes: "number >= 5",
});
const cancelForm = type({ reason: "string > 0" });
const closeoutForm = type({
	service_type: type.enumerated("Consulta", "Retorno", "Cirurgia", "Procedimento"),
	description: "string",
	has_payment: "boolean",
	method: type.enumerated("pix", "transfer", "cash", "debit_card", "credit_card"),
	amount: "number >= 0",
	"declared_installment_count?": type(`1 <= number.integer <= ${MAX_CARD_INSTALLMENTS}`),
}).narrow((value, ctx) => {
	if (
		value.has_payment &&
		value.method === "credit_card" &&
		value.declared_installment_count === undefined
	) {
		return ctx.mustBe("an installment count");
	}

	return !value.has_payment || value.amount > 0 || ctx.mustBe("a positive payment");
});

function RescheduleModal({ event, done }: { event: AgendaEvent; done: () => void }) {
	const queryClient = useQueryClient();
	const { mutate, isPending, error } = useMutation(
		orpc.agenda.update.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.agenda.key() });
				done();
			},
		}),
	);
	const { register, handleSubmit } = useForm<typeof rescheduleForm.infer>({
		resolver: arktypeResolver(rescheduleForm),
		defaultValues: {
			date: event.start_at.slice(0, 10),
			start_time: event.start_time,
			duration_minutes: Math.max(
				5,
				djs(`${event.start_at.slice(0, 10)}T${event.end_time}`).diff(djs(event.start_at), "minute"),
			),
		},
	});
	const errorCode = error instanceof ORPCError ? error.code : undefined;

	return (
		<OverlayShell label="Reagendar" onClose={done} className="items-center justify-center p-4">
			<form
				onSubmit={handleSubmit((value) => mutate({ hash: event.hash, ...value }))}
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<h3 className="type-section-title text-foreground">Reagendar</h3>
				<div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
					<Input {...register("date")} type="date" />
					<Input {...register("start_time")} type="time" />
					<Input
						{...register("duration_minutes", { valueAsNumber: true })}
						type="number"
						min={5}
						step={5}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" size="sm" variant="ghost" onClick={done}>
						Voltar
					</Button>
					<Button size="sm" disabled={isPending}>
						Salvar
					</Button>
				</div>
				{!!error && <p className="text-sm text-destructive">{AgendaLabels.saveError(errorCode)}</p>}
			</form>
		</OverlayShell>
	);
}

function CancelModal({ event, done }: { event: AgendaEvent; done: () => void }) {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.agenda.setStatus.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.agenda.key() });
				done();
			},
		}),
	);
	const { register, handleSubmit } = useForm<typeof cancelForm.infer>({
		resolver: arktypeResolver(cancelForm),
		defaultValues: { reason: "" },
	});

	return (
		<OverlayShell
			label="Cancelar horário"
			onClose={done}
			className="items-center justify-center p-4"
		>
			<form
				onSubmit={handleSubmit((value) =>
					mutate({ hash: event.hash, status: "cancelado", cancel_reason: value.reason }),
				)}
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<h3 className="type-section-title text-foreground">Cancelar horário</h3>
				<Input {...register("reason")} placeholder="Motivo" autoFocus />
				<div className="flex justify-end gap-2">
					<Button type="button" size="sm" variant="ghost" onClick={done}>
						Voltar
					</Button>
					<Button size="sm" variant="destructive" disabled={isPending}>
						Cancelar horário
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}

function CloseoutModal({ event, done }: { event: AgendaEvent; done: () => void }) {
	const queryClient = useQueryClient();
	const complete = useMutation(orpc.agenda.complete.mutationOptions());
	const {
		control,
		register,
		handleSubmit,
		trigger,
		watch,
		formState: { isValid },
	} = useForm<typeof closeoutForm.infer>({
		resolver: arktypeResolver(closeoutForm),
		mode: "onChange",
		defaultValues: {
			service_type: event.tipo === "Bloqueio" ? "Consulta" : event.tipo,
			description: "",
			has_payment: false,
			method: "pix",
			amount: 0,
			declared_installment_count: 1,
		},
	});
	const hasPayment = watch("has_payment");
	const paymentMethod = watch("method");
	const pending = complete.isPending;
	useEffect(() => {
		void trigger();
	}, [trigger]);
	const submit = handleSubmit(async (value) => {
		await complete.mutateAsync({
			hash: event.hash,
			service_type: value.service_type,
			description: value.description,
			payment: value.has_payment
				? {
						method: value.method,
						amount_cents: Math.round(value.amount * 100),
						declared_installment_count:
							value.method === "credit_card" ? value.declared_installment_count : undefined,
					}
				: undefined,
		});

		await queryClient.invalidateQueries({ queryKey: orpc.agenda.key() });
		await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
		done();
	});

	return (
		<OverlayShell
			label="Encerrar atendimento"
			onClose={done}
			className="items-center justify-center p-4"
		>
			<form onSubmit={submit} className="overlay-panel max-w-md gap-3 p-5">
				<h3 className="text-sm font-semibold">Encerrar atendimento</h3>
				<Controller
					control={control}
					name="service_type"
					render={({ field }) => (
						<Select {...field} className="h-9">
							<option>Consulta</option>
							<option>Retorno</option>
							<option>Cirurgia</option>
							<option>Procedimento</option>
						</Select>
					)}
				/>
				<Controller
					control={control}
					name="description"
					render={({ field }) => <Input {...field} placeholder="Descrição opcional" />}
				/>
				<label className="flex items-center gap-2 text-sm">
					<Controller
						control={control}
						name="has_payment"
						render={({ field }) => (
							<input
								ref={field.ref}
								name={field.name}
								type="checkbox"
								checked={field.value}
								onBlur={field.onBlur}
								onChange={(event) => field.onChange(event.target.checked)}
							/>
						)}
					/>
					Houve pagamento
				</label>
				{hasPayment && (
					<div className="grid grid-cols-2 gap-2">
						<Controller
							control={control}
							name="method"
							render={({ field }) => (
								<select {...field} className="control-field h-11 md:h-10">
									{Object.entries(PAYMENT_METHODS).map(([key, label]) => (
										<option key={key} value={key}>
											{label}
										</option>
									))}
								</select>
							)}
						/>
						<Controller
							control={control}
							name="amount"
							render={({ field }) => (
								<Input
									ref={field.ref}
									name={field.name}
									type="number"
									min="0.01"
									step="0.01"
									placeholder="Valor"
									value={Number.isNaN(field.value) ? "" : field.value}
									onBlur={field.onBlur}
									onChange={(event) => field.onChange(event.target.valueAsNumber)}
								/>
							)}
						/>
					</div>
				)}
				{hasPayment && paymentMethod === "credit_card" && (
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Parcelas
						<Input
							{...register("declared_installment_count", {
								valueAsNumber: true,
								shouldUnregister: true,
							})}
							type="number"
							min={1}
							max={MAX_CARD_INSTALLMENTS}
							step={1}
						/>
					</label>
				)}
				<div className="flex justify-end gap-2">
					<Button type="button" size="sm" variant="ghost" onClick={done}>
						Voltar
					</Button>
					<Button size="sm" disabled={pending || !isValid}>
						Encerrar
					</Button>
				</div>
				{complete.isError && (
					<p className="text-sm text-destructive">Não foi possível encerrar o atendimento.</p>
				)}
			</form>
		</OverlayShell>
	);
}

function StatusButton({
	event,
	status,
	label,
	done,
}: {
	event: AgendaEvent;
	status: LifecycleStatus;
	label: string;
	done: () => void;
}) {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.agenda.setStatus.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.agenda.key() });
				done();
			},
		}),
	);

	return (
		<Button
			size="sm"
			variant="outline"
			disabled={isPending}
			onClick={() => mutate({ hash: event.hash, status })}
		>
			{label}
		</Button>
	);
}

export function AgendaEventActions({ event, onDone }: { event: AgendaEvent; onDone: () => void }) {
	const [mode, setMode] = useState<"idle" | "reschedule" | "cancel" | "closeout">("idle");

	if (
		event.source !== "pulso" ||
		event.status === "cancelado" ||
		event.status === "cancelado_remoto" ||
		event.status === "finalizado" ||
		event.status === "faltou"
	) {
		return null;
	}

	return (
		<>
			<div className="flex flex-wrap justify-end gap-2">
				{event.status === "agendado" && (
					<StatusButton event={event} status="confirmado" label="Confirmar" done={onDone} />
				)}
				{event.status === "confirmado" && (
					<StatusButton event={event} status="em_espera" label="Chegou" done={onDone} />
				)}
				{event.status === "em_espera" && (
					<StatusButton event={event} status="em_atendimento" label="Iniciar" done={onDone} />
				)}
				{!event.bloqueio && (
					<StatusButton event={event} status="faltou" label="Faltou" done={onDone} />
				)}
				<Button size="sm" variant="outline" onClick={() => setMode("reschedule")}>
					Reagendar
				</Button>
				<Button size="sm" variant="outline" onClick={() => setMode("cancel")}>
					Cancelar
				</Button>
				{!event.bloqueio && (
					<Button size="sm" onClick={() => setMode("closeout")}>
						Encerrar
					</Button>
				)}
			</div>

			{mode === "reschedule" && <RescheduleModal event={event} done={() => setMode("idle")} />}
			{mode === "cancel" && <CancelModal event={event} done={onDone} />}
			{mode === "closeout" && <CloseoutModal event={event} done={onDone} />}
		</>
	);
}
